/**
 * HISI-006: Mission Card Component
 * Execution cards with mini pipeline visualization
 */

import React, { useEffect, useRef, useState } from 'react';
import { ExecutionStatus } from '@/types/execution';
import { formatCost } from '@/utils/format';
import { Zap, AlertTriangle, CheckCircle, Clock, MoreVertical, Pause, Play, Copy, Trash } from 'lucide-react';
import { useDashboardServerStore } from '@/stores/dashboard-server';

interface MissionCardProps {
  execution: ExecutionStatus;
  onClick?: () => void;
  className?: string;
}

const MissionCard: React.FC<MissionCardProps> = ({
  execution,
  onClick,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const animationFrameRef = useRef<number>();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { pauseExecution, resumeExecution, softDeleteExecution, duplicateExecution } = useDashboardServerStore();

  // Mini pipeline visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 200;
    canvas.height = 60;

    let time = 0;

    const stages = [
      'script', 'vision', 'storyboard', 
      'shots', 'continuity', 'frames', 'video'
    ];

    const currentStageIndex = stages.indexOf(execution.current_stage.toLowerCase()) || 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.02;

      // Draw pipeline stages
      const stageWidth = canvas.width / stages.length;
      
      stages.forEach((_, index) => {
        const x = index * stageWidth + stageWidth / 2;
        const y = canvas.height / 2;
        
        // Connection line
        if (index < stages.length - 1) {
          ctx.strokeStyle = index < currentStageIndex ? 
            'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + stageWidth, y);
          ctx.stroke();
        }

        // Stage node
        const isActive = index === currentStageIndex;
        const isComplete = index < currentStageIndex;
        const size = isActive ? 5 : 3;
        const pulse = isActive ? Math.sin(time * 3) * 2 : 0;

        // Glow effect
        if (isActive) {
          const gradient = ctx.createRadialGradient(x, y, 0, x, y, size + pulse + 10);
          gradient.addColorStop(0, 'rgba(6, 182, 212, 0.8)');
          gradient.addColorStop(1, 'transparent');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, size + pulse + 10, 0, Math.PI * 2);
          ctx.fill();
        }

        // Node
        ctx.fillStyle = isComplete ? '#10b981' : 
                        isActive ? '#06b6d4' : 
                        'rgba(255, 255, 255, 0.2)';
        ctx.beginPath();
        ctx.arc(x, y, size + pulse, 0, Math.PI * 2);
        ctx.fill();
      });

      // Progress wave
      if (execution.status === 'running') {
        const progress = execution.progress.overall;
        const waveX = canvas.width * progress;
        
        ctx.strokeStyle = 'rgba(6, 182, 212, 0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        
        for (let x = 0; x < waveX; x++) {
          const y = canvas.height / 2 + Math.sin((x / 20) + time * 2) * 5;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [execution]);

  // Status icon
  const getStatusIcon = () => {
    switch (execution.status) {
      case 'running':
        return <Zap className="w-4 h-4 text-cyan-400 animate-pulse" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-success-400" />;
      case 'failed':
        return <AlertTriangle className="w-4 h-4 text-error-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!(e.target instanceof Node)) return;
      if (!menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Quality color
  const getQualityColor = () => {
    const quality = execution.avg_quality_score;
    if (quality >= 80) return 'text-success-400';
    if (quality >= 60) return 'text-cyan-400';
    if (quality >= 40) return 'text-warning-400';
    return 'text-error-400';
  };

  return (
    <div
      className={`
        mission-card relative w-full h-48
        bg-gradient-to-br from-gray-900/50 to-gray-800/30
        backdrop-blur-md
        border border-white/5 rounded-lg
        transition-all duration-300 cursor-pointer
        hover:border-cyan-500/30 hover:shadow-cyan-glow
        hover:transform hover:scale-105 hover:-translate-y-1
        ${className}
      `}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <div className="p-4 pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <span className="text-xs font-mono text-white/50">
              {execution.id.slice(0, 8)}
            </span>
          </div>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              aria-label="Execution actions"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
              className={`p-1 rounded hover:bg-white/5 transition-colors ${isHovered ? 'text-cyan-400/70' : 'text-white/40'}`}
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-40 rounded-md border border-white/10 bg-gray-900/95 backdrop-blur-md shadow-lg z-20"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="py-1">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-cyan-500/10"
                    onClick={() => {
                      setMenuOpen(false);
                      if (execution.status === 'paused') {
                        resumeExecution(execution.id);
                      } else {
                        pauseExecution(execution.id);
                      }
                    }}
                  >
                    {execution.status === 'paused' ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    {execution.status === 'paused' ? 'Resume' : 'Pause'}
                  </button>

                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-cyan-500/10"
                    onClick={async () => {
                      setMenuOpen(false);
                      await duplicateExecution(execution.id);
                    }}
                  >
                    <Copy className="w-4 h-4" />
                    Duplicate
                  </button>

                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10"
                    onClick={async () => {
                      setMenuOpen(false);
                      await softDeleteExecution(execution.id);
                    }}
                  >
                    <Trash className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mini Pipeline Visualization */}
      <div className="px-4">
        <canvas
          ref={canvasRef}
          className="w-full h-15"
          style={{ imageRendering: 'pixelated' }}
        />
      </div>

      {/* Metrics */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pt-2">
        <div className="flex items-center justify-between text-xs font-mono">
          <div className="flex items-center gap-3">
            <span className="text-white/50">
              {execution.current_stage.toUpperCase()}
            </span>
            <span className={`led-readout ${getQualityColor()}`}>
              Q:{Math.round(execution.avg_quality_score)}
            </span>
          </div>
          <span className="text-cyan-400/70">
            {formatCost(execution.cost_incurred)}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mt-2 h-1 bg-black/50 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
            style={{ 
              width: `${execution.progress.overall * 100}%`,
              boxShadow: '0 0 10px rgba(6, 182, 212, 0.5)'
            }}
          />
        </div>
      </div>

      {/* Status glow effect */}
      {execution.status === 'running' && (
        <div className="absolute inset-0 rounded-lg pointer-events-none">
          <div className="absolute inset-0 rounded-lg animate-pulse bg-cyan-500/5" />
        </div>
      )}
    </div>
  );
};

export default MissionCard;